import { useContext } from "react";
import { GlobalContext } from "@/components/Theme";
import { useState, useEffect, useRef } from "react";
import { IS_LIVE } from "@/utils/constants";
import H1 from "@/atoms/text/H1";
import Para from "@/atoms/text/Para";
import Image from "@/atoms/Image";
import Button from "@/atoms/Button";
import { isMobileScreen } from "@/utils/functions";
import { UlCheckedIcon, TreesFilledIcon } from "@/atoms/Icons";
import Trustpilot from "@/organisms/Trustpilot";
import RegistrationForm from "@/molecules/RegistrationForm";
import VideoModalTrigger from "@/components/molecules/VideoModalTrigger";
import PropTypes from "prop-types";
import BasicPlans from "@/molecules/BasicPlans";
import FranchisePlans from "@/molecules/FranchisePlans";

export default function Hero({
  heroTitle,
  heroContent,
  variant = "default",
  listItems,
  heroImgData,
  hasPricingCta,
  hasTrustpilot,
  hasScrollToIntro = "true",
  hasVideoModalTrigger,
  hasComponent,
  videoSrc,
  isFranchisePlans = false,
  isFormStaticOnScroll = false,
  hasTreeCounter = false,
  regFormOptions = {
    bgColor: "white",
    title: false,
    hasBusinessNameField: true,
    hasFormCardTitle: false,
  },
}) {
  const [isMobile, setIsMobile] = useState(null);

  const heroRef = useRef(null);

  const context = useContext(GlobalContext);
  const treeCount = context?.[2];

  useEffect(() => {
    setIsMobile(isMobileScreen);
  }, []);

  const handleSmoothScroll = (e) => {
    const mainElement = e.target.closest(".webpage").querySelector("main");
    mainElement.scrollIntoView({ behavior: "smooth" });
  };

  if (variant === "default") {
    const { fileName, alt } = heroImgData;

    return (
      <section className="hero" ref={heroRef}>
        <article className="container fx fx--c fx--sa">
          <div className="hero--content w-50-d pt-reset-d mt-reset-d mt-10 pt-4">
            <H1
              color="white"
              mobileSize="sm"
              className="bold mb-5"
              dangerouslySetInnerHTML={{ __html: heroTitle }}
            />
            <Para
              color="white"
              desktopSize="lg"
              mobileSize="lg"
              dangerouslySetInnerHTML={{ __html: heroContent }}
              className="mb-2 mb-reset-d"
            />
            {hasPricingCta && (
              <div
                className={`${
                  hasTrustpilot ? "adjust-height" : ""
                } fx-d fx--cv mt-1-d`}
              >
                <Button
                  href={
                    IS_LIVE
                      ? "https://www.answerforce.com/register"
                      : "https://testing.answerforce.com/register"
                  }
                  size="md"
                  className="mt-5 mt-reset-d mr-7-d"
                >
                  See Prices
                </Button>
                {hasTrustpilot && <Trustpilot variant="mini" />}
              </div>
            )}
          </div>
          {!isMobile && (
            <button
              onClick={handleSmoothScroll}
              className="desktop-only btn btn--link__scroll btn--link-lg scroll-text text--md to-left"
            >
              Learn more
            </button>
          )}
        </article>

        <Image
          fileName={fileName}
          alt={alt}
          fileFormat="jpg"
          mobileWidth="375"
          className="hero--bkg"
          isLazyLoad={false}
        />
        <style jsx>{`
          @media (max-width: 749px) {
            .adjust-height {
              height: 166px;
            }
          }
        `}</style>
      </section>
    );
  }

  if (variant === "gybnmHero") {
    const { fileName, alt } = heroImgData;

    return (
      <section className="hero has-bgImg-mob ps-r" ref={heroRef}>
        <article className="ps-r container fx fx--c fx-v fx--sa">
          <div
            className={`hero--content pt-reset-d mt-reset-d ${
              hasTreeCounter ? "mb-reset-d mb-20" : "mt-10"
            } pt-4 w-100 ta-center`}
          >
            {heroTitle && (
              <H1
                color="white"
                mobileSize="sm"
                className={`bold ${hasTreeCounter ? "mb-3" : "mb-5"}`}
                dangerouslySetInnerHTML={
                  !hasComponent ? { __html: heroTitle } : null
                }
              >
                {hasComponent && heroTitle}
              </H1>
            )}
            {heroContent && (
              <Para
                color="white"
                desktopSize="lg"
                mobileSize="lg"
                dangerouslySetInnerHTML={{ __html: heroContent }}
                className={`${
                  hasTreeCounter
                    ? "mb-20"
                    : "mb-2 mb-reset-d letter-space-xxl text-uppercase fw-semibold"
                }`}
              />
            )}
            {hasPricingCta && (
              <div
                className={`${
                  hasTrustpilot ? "adjust-height" : ""
                } fx-d fx--cv mt-1-d`}
              >
                <Button
                  href={
                    IS_LIVE
                      ? "https://www.answerforce.com/register"
                      : "https://testing.answerforce.com/register"
                  }
                  size="md"
                  className="mt-5 mt-reset-d mr-7-d"
                >
                  See Prices
                </Button>
                {hasTrustpilot && <Trustpilot variant="mini" />}
              </div>
            )}
            {hasVideoModalTrigger && (
              <VideoModalTrigger variant="btnWithIcon" src={videoSrc} />
            )}
          </div>
          {hasTreeCounter && (
            <div className="ps-a bottom-120 fx fx--c">
              <div className={`hero--content translate-center`}>
                <div className="fx--ch fx-v">
                  <TreesFilledIcon
                    transformTree="translate(0 0)"
                    className="ps-r bottom-5 mr-2"
                    width={isMobile ? "50" : "60"}
                    height={isMobile ? "40" : "48"}
                  />
                  <span className="bold title title--lg-d title--md tc-white">
                    {treeCount}
                  </span>
                </div>
                <span className="ml-6-d ml-2 text text--lg tc-white">
                  trees planted and counting
                </span>
              </div>
            </div>
          )}
          {!isMobile && hasScrollToIntro && (
            <button
              onClick={handleSmoothScroll}
              className="desktop-only btn btn--link__scroll btn--link-lg scroll-text text--md to-left"
            >
              Learn more
            </button>
          )}
        </article>
        <Image
          fileName={fileName}
          alt={alt}
          fileFormat="jpg"
          mobileWidth="375"
          className="hero--bkg"
        />
        <style jsx>{`
          @media (max-width: 749px) {
            .adjust-height {
              height: 166px;
            }
          }
          @media (max-width: 649px) {
            .hero--content.translate-center {
              position: absolute;
            }
          }
        `}</style>
      </section>
    );
  }

  if (variant === "legalInfoPage") {
    return (
      <section className="hero bc-grad-blue" ref={heroRef}>
        <article className="container fx fx--cv fx--ch">
          <H1
            color="white"
            mobileSize="sm"
            className="bold"
            dangerouslySetInnerHTML={{ __html: heroTitle }}
          />
        </article>
        <style jsx>{`
          .hero {
            max-height: 374px;
          }
          .hero::before {
            content: none;
          }
          @media (max-width: 749px) {
            .hero .container {
              min-height: 280px;
            }
          }
        `}</style>
      </section>
    );
  }

  if (variant === "heroWithForm") {
    return (
      <section
        className={`hero bc-grad-blue ${
          isFormStaticOnScroll ? "has-static-form" : "has-form"
        }`}
        ref={heroRef}
      >
        <div className="container">
          <div className="w-100 pt-reset-d mt-reset-d pt-10 pt-4">
            <div
              className={`fx-d fx--sb fx--c fx--r-d hero-content ${
                isFormStaticOnScroll ? "" : "fx--cv"
              }`}
            >
              <article className="w-50-d mb-6 mb-reset-d content-wrapper">
                <div>
                  <H1
                    color="white"
                    desktopSize={`${isFormStaticOnScroll ? "lg" : "xl"}`}
                    mobileSize="sm"
                    className="bold mb-5"
                    dangerouslySetInnerHTML={{ __html: heroTitle }}
                  />
                  <Para
                    color="white"
                    desktopSize="lg"
                    mobileSize="lg"
                    dangerouslySetInnerHTML={{ __html: heroContent }}
                  />
                  {listItems && (
                    <ul className="mv-5">
                      {listItems.map((listItem, idx) => {
                        return (
                          <li
                            key={idx}
                            className={`fx fx--cv-d mb-2 ${
                              idx === listItems.length - 1 ? "mb-reset" : ""
                            } `}
                          >
                            <UlCheckedIcon
                              color="#61CEFC"
                              className="mt-2 mt-reset-d mr-2"
                            />
                            <Para
                              desktopSize="lg"
                              mobileSize="lg"
                              color="white"
                            >
                              {listItem}
                            </Para>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                  <div className="trustpilot-container">
                    <Trustpilot variant="mini" />
                  </div>
                </div>
              </article>
              <RegistrationForm
                bgColor={regFormOptions.bgColor}
                title={regFormOptions.title}
                btnName={regFormOptions.btnName}
                hasFormCardTitle={regFormOptions.hasFormCardTitle}
                hasBusinessNameField={regFormOptions.hasBusinessNameField}
                formOnHero={regFormOptions.formOnHero}
                formUniqueName="hero-form"
              />
            </div>
          </div>
          {!isMobile && hasScrollToIntro && (
            <button
              onClick={handleSmoothScroll}
              className="desktop-only btn btn--link__scroll btn--link-lg scroll-text text--md to-left"
            >
              Learn more
            </button>
          )}
        </div>
        <style jsx>{`
          .trustpilot-container {
            height: 120px;
          }
          @media (max-width: 500px) {
            .hero.has-form {
              padding-bottom: 0;
            }
          }
          @media (max-width: 1199px) {
            .container {
              padding: 0;
            }
            .content-wrapper {
              padding: 0 20px;
            }
          }
        `}</style>
      </section>
    );
  }

  if (variant === "subIntegrationHero") {
    const { fileName, alt } = heroImgData;
    return (
      <section className="hero ps-r">
        <div className="container">
          <article className="ta-center-d pt-20-d pt-10">
            <H1
              color="white"
              mobileSize="sm"
              className="bold mb-5-d mb-3 mt-8-d mt-4"
              dangerouslySetInnerHTML={{ __html: heroTitle }}
            />
            <Para
              color="white"
              desktopSize="lg"
              mobileSize="lg"
              className="mb-10-d mb-7"
              dangerouslySetInnerHTML={{ __html: heroContent }}
            />
          </article>
          <Image
            fileName={fileName}
            alt={alt}
            fileFormat="jpg"
            desktopWidth="100%"
            mobileWidth="100%"
            imgStyle={`${isMobile ? "br-16" : "br-20"}`}
          />
        </div>
        <style jsx>{`
          .hero {
            height: auto;
          }
          .hero::before {
            background: linear-gradient(
              90deg,
              #2e3192 -6.49%,
              #00aded 105.6%,
              #00aded 105.6%
            );
            content: "";
            height: 75%;
            width: 100%;
            position: absolute;
            top: 0;
            left: 0;
            z-index: -1;
          }
        `}</style>
      </section>
    );
  }

  if (variant === "plansHero") {
    return (
      <>
        <section className="hero-plans ps-r bc-blue-light pb-10">
          <div className="section-grad-top bc-grad-blue"></div>
          <div className="container">
            <article className="ta-center-d pt-20-d pt-10">
              <H1
                color="white"
                mobileSize="sm"
                className="bold mb-5-d mb-3 mt-reset-d mt-5"
                dangerouslySetInnerHTML={{ __html: heroTitle }}
              />
              <Para
                color="white"
                desktopSize="lg"
                mobileSize="lg"
                className="mb-8"
                dangerouslySetInnerHTML={{ __html: heroContent }}
              />
            </article>
            {isFranchisePlans ? <FranchisePlans /> : <BasicPlans />}
          </div>
        </section>
        <style jsx>
          {`
            .hero-plans {
              z-index: 1;
            }
            .section-grad-top {
              content: "";
              position: absolute;
              top: 0;
              left: 0;
              right: 0;
              height: 740px;
              z-index: -1;
            }
            @media (max-width: 1024px) {
              .section-bg-top {
                height: 600px;
              }
            }
          `}
        </style>
      </>
    );
  }
}

Hero.propTypes = {
  heroImgData: PropTypes.shape({
    fileName: PropTypes.string,
    alt: PropTypes.string,
  }),
};
